// @ts-expect-error not typed yet

import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import { H2 } from 'UIComponents/elements/headings';
import UIForm from 'UIComponents/form/UIForm';
import UIButtonWrapper from 'UIComponents/layout/UIButtonWrapper';
import UIPanel from 'UIComponents/panel/UIPanel';
import UIPanelBody from 'UIComponents/panel/UIPanelBody';
import UIPanelFooter from 'UIComponents/panel/UIPanelFooter';
import UIPanelHeader from 'UIComponents/panel/UIPanelHeader';
import UIPanelSection from 'UIComponents/panel/UIPanelSection';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import Raven from 'raven-js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFetchResolver } from 'reference-resolvers-lite/ResolverHooks';
import { TEAM } from 'reference-resolvers-lite/constants/ReferenceTypes';
import { createStandardCachedResolver } from 'reference-resolvers-lite/utils/createStandardCachedResolver';
import isNumber from 'transmute/isNumber';
import getBUFromLocalStorage from 'EmailComponents/utils/getBUFromLocalStorage';
import { AB_SELECT_VALUE } from 'EmailData/constants/details/abEmailConstants';
import EMAIL_TEMPLATE_MODE from 'EmailData/enums/EmailTemplateModes';
import ABEmailCloneSelection from 'email-management-lib/EmailClone/components/ABEmailCloneSelection';
import PrimaryClonePanelBody from 'email-management-lib/EmailClone/components/PrimaryClonePanelBody';
import { BusinessUnitsRebrandMessage } from 'ui-business-units-lib/components/BusinessUnitsRebrandMessage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const MAX_EMAIL_NAME_LENGTH = 200;
function EmailCloneSidePanel({
  email,
  emailDetails,
  isConvertible,
  onClose,
  smartContentLocations,
  variantSmartContentLocations,
  hasCustomPlaintext,
  variantHasCustomPlaintext,
  canAccessBusinessUnit,
  assignedCustomProperties,
  onClone
}) {
  const suffix = I18n.text('email-management.emailClone.cloneSidePanel.clonedContentSuffix');
  const aSuffix = I18n.text('email-management.emailClone.cloneSidePanel.clonedVersionASuffix');
  const bSuffix = I18n.text('email-management.emailClone.cloneSidePanel.clonedVersionBSuffix');
  const abSuffix = I18n.text('email-management.emailClone.cloneSidePanel.clonedABSuffix');
  const resolver = createStandardCachedResolver(TEAM);
  const name = email.name;
  const [emailName, setEmailName] = useState(`${email.name} ${suffix}`);
  const [selectedEmailType, setSelectedEmailType] = useState(null);
  const [selectedTemplateMode, setSelectedTemplateMode] = useState(email.emailTemplateMode);
  const [selectedABVersion, setSelectedABVersion] = useState(null);
  const [id, setId] = useState(email.id);
  const [isVariantOnly, setIsVariantOnly] = useState(false);
  const [cloneWithSmartContent, setCloneWithSmartContent] = useState(smartContentLocations.length > 0);
  const [cloneWithCustomPlaintext, setCloneWithCustomPlaintext] = useState(false);
  const [retainPermissionsAfterClone, setRetainPermissionsAfterClone] = useState(true);
  const [buId, setBuId] = useState(() => {
    if (!canAccessBusinessUnit) {
      return null;
    }
    return parseInt(getBUFromLocalStorage(), 10);
  });
  const [isCloneLoading, setIsCloneLoading] = useState(false);
  const teamPerms = useMemo(() => email.teamPerms, [email]);
  const userPerms = useMemo(() => email.userPerms, [email]);
  const teamCount = useMemo(() => {
    var _teamPerms$length;
    return (_teamPerms$length = teamPerms.length) !== null && _teamPerms$length !== void 0 ? _teamPerms$length : 0;
  }, [teamPerms]);
  const userCount = useMemo(() => {
    var _userPerms$length;
    return (_userPerms$length = userPerms.length) !== null && _userPerms$length !== void 0 ? _userPerms$length : 0;
  }, [userPerms]);
  const {
    reference
  } = useFetchResolver(resolver, String(teamPerms[0]));
  const teamName = useMemo(() => {
    return teamCount === 0 || !reference ? undefined : reference.label;
  }, [reference, teamCount]);
  const hasInvalidSmartContentLocations = useMemo(() => {
    const anyInvalidLocation = smartContentLocations.find(location => {
      return location.isValid === false;
    });
    return anyInvalidLocation !== undefined;
  }, [smartContentLocations]);
  useEffect(() => {
    if (selectedABVersion === AB_SELECT_VALUE.AB) {
      setCloneWithSmartContent(true);
      setCloneWithCustomPlaintext(true);
    }
  }, [selectedABVersion]);
  const onSelectType = useCallback(type => {
    setSelectedEmailType(type);
  }, []);
  const onSelectTemplateMode = useCallback(mode => {
    setSelectedTemplateMode(mode);
  }, []);
  const onChangeEmailName = useCallback(e => setEmailName(e.target.value), []);
  const onSelectAb = useCallback(({
    variantOnly,
    contentId,
    version
  }) => {
    setId(contentId);
    setSelectedABVersion(version);
    setIsVariantOnly(variantOnly);
    switch (version) {
      case AB_SELECT_VALUE.A:
        setEmailName(`${name} ${aSuffix}`);
        break;
      case AB_SELECT_VALUE.B:
        setEmailName(`${name} ${bSuffix}`);
        break;
      case AB_SELECT_VALUE.AB:
        setEmailName(`${name} ${abSuffix}`);
        break;
      default:
        break;
    }
  }, [aSuffix, abSuffix, bSuffix, name]);
  const onSelectSmartContent = useCallback(checked => {
    setCloneWithSmartContent(checked);
  }, []);
  const onSelectPlaintext = useCallback(checked => {
    setCloneWithCustomPlaintext(checked);
  }, []);
  const onSelectPermissions = useCallback(checked => {
    setRetainPermissionsAfterClone(checked);
  }, []);
  const onSelectBusinessUnit = useCallback(_id => {
    setBuId(_id);
  }, []);
  const maybeRenderAbCloneOptions = useMemo(() => {
    if (emailDetails && !emailDetails.isEmpty() && email.isABLike()) {
      return /*#__PURE__*/_jsx(ABEmailCloneSelection, {
        emailDetails: emailDetails,
        onSelect: onSelectAb
      });
    } else {
      return null;
    }
  }, [email, emailDetails, onSelectAb]);
  const handleClick = useCallback(() => {
    setIsCloneLoading(true);
    try {
      onClone(selectedTemplateMode, {
        cloneEmailName: emailName,
        emailType: selectedEmailType,
        contentId: id,
        variantOnly: isVariantOnly,
        cloneWithSmartContent,
        cloneWithCustomPlaintext,
        retainPermissionsAfterClone,
        businessUnitId: buId
      });
    } catch (e) {
      Raven.captureException(e);
      setIsCloneLoading(false);
    }
  }, [onClone, selectedTemplateMode, emailName, selectedEmailType, id, isVariantOnly, cloneWithSmartContent, cloneWithCustomPlaintext, retainPermissionsAfterClone, buId]);
  const cloneButtonTooltipDisabledMessage = useMemo(() => {
    if (!emailName) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "email-management.emailClone.cloneSidePanel.buttons.tooltip.noName"
      });
    }
    if (emailName.length > MAX_EMAIL_NAME_LENGTH) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "email-management.emailClone.cloneSidePanel.buttons.tooltip.nameTooLong"
      });
    }
    if (email.isABLike() && !selectedABVersion) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "email-management.emailClone.cloneSidePanel.buttons.tooltip.noABVersion"
      });
    }
    if ((email.isBatch() || email.isAutomated()) && !selectedEmailType) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "email-management.emailClone.cloneSidePanel.buttons.tooltip.noType"
      });
    }
    if (email.emailTemplateMode === EMAIL_TEMPLATE_MODE.DESIGN_MANAGER && isConvertible && !selectedTemplateMode) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "email-management.emailClone.cloneSidePanel.buttons.tooltip.noMode"
      });
    }
    if (canAccessBusinessUnit && !isNumber(buId)) {
      return /*#__PURE__*/_jsx(BusinessUnitsRebrandMessage, {
        message: "email-management.emailClone.cloneSidePanel.buttons.tooltip.noBU",
        rebrandMessage: "email-management.emailClone.cloneSidePanel.buttons.tooltip.noBU_buv2"
      });
    }
    if (hasInvalidSmartContentLocations) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "email-management.emailClone.cloneSidePanel.buttons.tooltip.invalidSmartContent"
      });
    }
    return null;
  }, [buId, canAccessBusinessUnit, email, emailName, hasInvalidSmartContentLocations, isConvertible, selectedABVersion, selectedEmailType, selectedTemplateMode]);
  return /*#__PURE__*/_jsx(UIPanel, {
    width: 450,
    "data-test-id": "email-clone-side-panel",
    children: /*#__PURE__*/_jsxs(UIForm, {
      children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: onClose
      }), /*#__PURE__*/_jsx(UIPanelHeader, {
        children: /*#__PURE__*/_jsx(H2, {
          children: email.isABLike() ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "email-management.emailClone.cloneSidePanel.abTitle"
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "email-management.emailClone.cloneSidePanel.title"
          })
        })
      }), /*#__PURE__*/_jsx(UIPanelBody, {
        children: /*#__PURE__*/_jsxs(UIPanelSection, {
          children: [maybeRenderAbCloneOptions, /*#__PURE__*/_jsx(PrimaryClonePanelBody, {
            email: email,
            selectedABVersion: selectedABVersion,
            emailName: emailName,
            onChangeEmailName: onChangeEmailName,
            canAccessBusinessUnit: canAccessBusinessUnit,
            onSelectBusinessUnit: onSelectBusinessUnit,
            buId: buId,
            selectedEmailType: selectedEmailType,
            onSelectType: onSelectType,
            isConvertible: isConvertible,
            onSelectTemplateMode: onSelectTemplateMode,
            selectedTemplateMode: selectedTemplateMode,
            smartContentLocations: smartContentLocations,
            variantSmartContentLocations: variantSmartContentLocations,
            hasCustomPlaintext: hasCustomPlaintext,
            variantHasCustomPlaintext: variantHasCustomPlaintext,
            onSelectPermissions: onSelectPermissions,
            onSelectPlaintext: onSelectPlaintext,
            onSelectSmartContent: onSelectSmartContent,
            cloneWithSmartContent: cloneWithSmartContent,
            cloneWithCustomPlaintext: cloneWithCustomPlaintext,
            retainPermissionsAfterClone: retainPermissionsAfterClone,
            teamName: teamName,
            teamCount: teamCount,
            userCount: userCount,
            assignedCustomProperties: assignedCustomProperties
          })]
        })
      }), /*#__PURE__*/_jsx(UIPanelFooter, {
        children: /*#__PURE__*/_jsxs(UIButtonWrapper, {
          children: [cloneButtonTooltipDisabledMessage ? /*#__PURE__*/_jsx(UITooltip, {
            title: cloneButtonTooltipDisabledMessage,
            children: /*#__PURE__*/_jsx(UIButton, {
              "data-test-id": "clone-confirm-button-disabled",
              disabled: true,
              use: "primary",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "email-management.emailClone.cloneSidePanel.buttons.clone"
              })
            })
          }) : /*#__PURE__*/_jsx(UILoadingButton, {
            "data-test-id": "clone-confirm-button",
            use: "primary",
            onClick: handleClick,
            loading: isCloneLoading,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "email-management.emailClone.cloneSidePanel.buttons.clone"
            })
          }), /*#__PURE__*/_jsx(UIButton, {
            onClick: onClose,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "email-management.emailClone.cloneSidePanel.buttons.cancel"
            })
          })]
        })
      })]
    })
  });
}
export default EmailCloneSidePanel;